import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';

/** Avoid stacked microtasks from parallel 401 responses. */
let handlingUnauthorized401 = false;

/**
 * When the API returns 401 and a token is still in storage (e.g. revoked after
 * password reset elsewhere), clear client session and send the user to sign-in.
 * Uses Injector + queueMicrotask so AuthService (which depends on HttpClient) is not
 * constructed during HttpClient bootstrap (circular DI).
 */
export const auth401Interceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      if (!localStorage.getItem('token')) {
        return throwError(() => err);
      }

      if (!handlingUnauthorized401) {
        handlingUnauthorized401 = true;
        queueMicrotask(() => {
          try {
            injector.get(AuthService).invalidateSessionDueToUnauthorized();
            const router = injector.get(Router);
            const ngZone = injector.get(NgZone);
            ngZone.run(() => {
              const path = router.url.split('?')[0];
              // OAuth callbacks must finish without forcing signin navigation.
              if (path.startsWith('/auth')) {
                return;
              }
              router.navigate(['/signin'], {
                replaceUrl: true,
                queryParams: { reason: 'session' },
              });
            });
          } catch (e) {
            console.error('auth401Interceptor cleanup failed', e);
          } finally {
            handlingUnauthorized401 = false;
          }
        });
      }

      return throwError(() => err);
    })
  );
};
